// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSelector } from 'reselect';
import type { RootState } from 'Store';
import { selectSelectedBundle } from 'Features/bundle/bundleSelectors';

const getOrdersList = (state: RootState) => state.order.list;
const getOrderId = (state: RootState) => state.order.orderId;
const getProjectId = (state: RootState) => state.project.projectId;
const getSelectedBundle = (state: RootState) => state.bundle.selectedBundle;
const getUploadOwnData = (state: RootState) => state.bundle.uploadOwnData;
const getSurchargePolicy = (state: RootState) => state.bundle.surchargePolicy;

const round2 = (n: number): number => Math.round(n * 100) / 100;

const getOrders = createSelector(
  [getProjectId, getOrdersList],
  (projectId, orders) => {
    const stateOrders = orders.filter((o) => o.ownerProject === projectId);
    const projectOrders = stateOrders.sort((order1, order2) => ((order1.createdAt < order2.createdAt) ? 1 : -1));
    const projectOrdersReverse = stateOrders.slice().sort((order1, order2) => (
      (order1.createdAt > order2.createdAt) ? 1 : -1
    ));
    const ordersWithCadFiles = projectOrders.filter((o) => o.cadFiles && o.cadFiles.length > 0);
    const nonActiveOrders = projectOrders.filter((o) => o.orderStatus > 0);
    // TODO: use orderStatusCode constants instead of hardcoded values
    const activeOrder = projectOrders.find((o) => o.orderStatus === 0);
    const placedOrder = projectOrders.find((o) => o.orderStatus >= 1);
    const placedOrders = projectOrders.filter((o) => o.orderStatus >= 1);
    const ordersWithOutputs = projectOrders.filter((o) => o.orderStatus === 7 && (o.cadFiles?.length > 0 || o.outputLasFiles?.some((las) => las.tile3DUrl)));

    const result = {
      projectOrders,
      projectOrdersReverse,
      ordersWithCadFiles,
      nonActiveOrders,
      activeOrder,
      placedOrder,
      placedOrders,
      ordersWithOutputs,
    };

    return result;
  },
);

const selectCurrentOrder = createSelector(
  [getOrders, (_, orderId) => orderId],
  (orders, orderId) => {
    const currentOrder = orders.ordersWithCadFiles.find((order) => order._id === orderId);
    return currentOrder;
  },
);

// Selector to evaluate the cases for viewing the CAD view tab
const cadViewPermissions = createSelector(
  [getProjectId, getOrdersList],
  (projectId, orders) => {
    const projectOrders = orders.filter((o) => o.ownerProject === projectId);

    const orderWithExpressAIFile = (projectOrders.some((order) => order.cadFiles && order.cadFiles.some((file) => (file?.expressAIUpload && file?.expressAIEmailSent))));

    const fullyCompletedOrders = projectOrders.some((order) => order.orderStatus === 7);
    const autonomousOrFullyCompletedOrders = projectOrders.some((order) => order.orderStatus === 7 || order.orderStatus === 2);

    const cadViewProd = orderWithExpressAIFile || fullyCompletedOrders;
    const cadViewDevStage = orderWithExpressAIFile || autonomousOrFullyCompletedOrders;
    const result = {
      cadViewProd,
      cadViewDevStage,
    };

    return result;
  },
);

const getOrderIds = createSelector(
  [getProjectId, getOrdersList],
  (projectId, orders) => {
    const projectOrders = orders.filter((o) => o.ownerProject === projectId);

    return projectOrders.map((o) => o._id);
  },
);

// Selector to get parent project for an order
const getOrderOwnerProject = createSelector(
  [getOrderId, getOrdersList],
  (orderId, orders) => {
    const order = orders.find((o) => o._id === orderId);
    return order.ownerProject;
  },
);

const getProcessingOrders = createSelector(
  [getOrdersList],
  (orders) => {
    if (!orders) return [];
    // orders with orderStatus from 1 through 6 will show the pizza tracker on the project card
    return orders.filter((o) => [1, 2, 3, 4, 5, 6].includes(o.orderStatus));
  },
);

// Derives the utilities catalog (utilities_product) cost breakdown the order sidebar renders:
// subtotal → tier discount → surcharge → total → credits. The dollar math the server can't
// pre-compute (it doesn't know the FE's upload-own-data / extra-layer choices) lives here so
// the component stays render-only. isCatalogProduct is a parameter because the bundle-type
// selection is component state (the bundle-type tabs), not Redux.
const selectCatalogPricing = createSelector(
  [getSelectedBundle, getUploadOwnData, getSurchargePolicy, selectSelectedBundle, (_: RootState, isCatalogProduct: boolean) => isCatalogProduct],
  (selectedBundle, uploadOwnData, surchargePolicy, { extraLayersLength }, isCatalogProduct) => {
    const bundleCost = selectedBundle?.bundleCost ?? 0;
    const credit = selectedBundle?.priceCredit ?? 0;
    const dollarPerCredit = selectedBundle?.dollarPerCredit ?? 0;

    // Charge the server-computed extraction-only price when the customer uploads their own
    // data, otherwise the full product price (extraction + data).
    const extractionDollar = uploadOwnData ? (selectedBundle?.extractionDollar ?? 0) : bundleCost;

    // The subtotal drops the data portion too so "subtotal − discount + surcharge = total" holds.
    const dataDollar = uploadOwnData ? (selectedBundle?.dataProviderDollar ?? 0) : 0;
    const subtotalDollar = Math.max(0, (selectedBundle?.priceBeforeDiscountDollar ?? 0) - dataDollar);

    // Additional-layer rule, per the policy the catalog API supplies: layers beyond the free
    // allowance each add a percentage of the product's extraction charge. Additional
    // deliverables are free. The base is the extraction charge, not the full product price,
    // so the surcharge doesn't move when the customer supplies their own data — matching what
    // the server charges at checkout.
    const appliesToSubtype = !!surchargePolicy?.appliesToSubtypes.includes(selectedBundle?.subtype ?? '');
    const extraLayers = appliesToSubtype ? Math.max(0, extraLayersLength - surchargePolicy.freeAllowance) : 0;
    const surchargeDollar = isCatalogProduct && surchargePolicy
      ? round2(extraLayers * surchargePolicy.rate * extractionDollar)
      : 0;

    // The deployment/minimum fee floors the dollar total for a small non-exempt project. It does not
    // add credits (the server's creditAcreage is pre-fee), so credits use the pre-fee total.
    const deploymentFeeDollar = isCatalogProduct ? (selectedBundle?.deploymentFeeDollar ?? 0) : 0;
    const licenseFeeDollar = isCatalogProduct ? (selectedBundle?.licenseFeeDollar ?? 0) : 0;
    const preFeeTotalDollar = round2(extractionDollar + surchargeDollar);
    const totalCredit = dollarPerCredit > 0 ? round2(preFeeTotalDollar / dollarPerCredit) : credit;
    const totalDollar = round2(preFeeTotalDollar + deploymentFeeDollar + licenseFeeDollar);

    return {
      // Product has no price (e.g. Landbase Premium) → sidebar shows "Contact Sales for Pricing".
      contactForPricing: isCatalogProduct && !!selectedBundle?.contactForPricing,
      extractionDollar,
      subtotalDollar,
      // The tier discount already applied to the extraction+data figures the server returned.
      discountDollar: selectedBundle?.discountDollar ?? 0,
      // The active promotion's dollars off, also already inside the server's figures: the promotion floors the total
      // discount at its percent, so this is the top-up past the tier discount (0 when the tier already reaches it).
      promotionDollar: isCatalogProduct ? (selectedBundle?.promotionDollar ?? 0) : 0,
      tierName: selectedBundle?.tierName || '',
      // Nameless-of-thresholds upsell hint for this order's size; null when there's no next tier.
      nextTierTeaser: selectedBundle?.nextTierTeaser ?? null,
      // The size band the extraction price used; null for a non-banded product.
      sizeBand: selectedBundle?.sizeBand ?? null,
      // Buffer-extension multiplier applied to the extraction (corridor products); 1 when none.
      bufferMultiplier: selectedBundle?.bufferMultiplier ?? 1,
      // Deployment / minimum project fee floored onto the total; 0 when none applies.
      deploymentFeeDollar,
      // The 10% license fee added for a committed, not-prepaying org; 0 when none.
      licenseFeeDollar,
      dollarPerCredit,
      extraLayers,
      surchargeDollar,
      totalDollar,
      totalCredit,
    };
  },
);

export {
  getOrders,
  selectCurrentOrder,
  getOrderIds,
  getOrderOwnerProject,
  getProcessingOrders,
  cadViewPermissions,
  selectCatalogPricing,
};
